<template>
    <div>
        <div class="modules-header" style="display: flex; justify-content: center;">
            <span class="modules-title">Módulos y Suscripciones</span>
        </div>
        <div class="modules-container">
            <div
v-for="module in modules" :key="module.id"
                :class="['module-card', { 'module-disabled': !isSubscriptionActive(module.id) }]">
                <div class="module-content">
                    <span class="module-icon">
                        <i class="ni ni-box-2"></i>
                    </span>

                    <div class="module-info">
                        <span class="module-name">{{ module.name }}</span>
                        <small v-if="module.description" class="module-description">{{ module.description }}</small>
                        
                        <!-- Información de la suscripción si está activa -->
                        <div v-if="getSubscription(module.id)" class="subscription-info">
                            <small class="module-users">
                                <i class="fas fa-users"></i> 
                                {{ getSubscription(module.id).current_users }}/{{ getSubscription(module.id).contracted_users }} médicos
                            </small>
                            <small class="module-price">
                                <i class="fas fa-dollar-sign"></i> 
                                ${{ formatPrice(getSubscription(module.id).final_price) }}/mes
                                <span v-if="getSubscription(module.id).billing_cycle !== 'monthly'" style="font-size: 0.75rem; color: #6b7280;">
                                    (Facturado {{ getBillingCycleLabel(getSubscription(module.id).billing_cycle).toLowerCase() }})
                                </span>
                            </small>
                            <span :class="['status-badge', getSubscription(module.id).status]">
                                {{ getStatusLabel(getSubscription(module.id).status) }}
                            </span>
                        </div>
                        <!-- Información del módulo si no está suscrito -->
                        <div v-else class="module-pricing-info">
                            <small v-if="module.trial_days" class="module-trial">
                                <i class="fas fa-gift"></i> {{ module.trial_days }} días de prueba gratis
                            </small>
                            <small class="module-price">
                                <i class="fas fa-dollar-sign"></i> 
                                <b>${{ formatPrice(module.pricing_info?.price_per_user || 0) }}</b>/mes por médico
                            </small>
                            <small class="module-description">Precio uniforme - Sin complicaciones</small>
                        </div>
                    </div>
                </div>

                <div class="module-action">
                    <ToggleSwitch
v-model="moduleStates[module.id]" :errors="errors"
                        :error-field-name="`module-${module.id}`"
                        @update:model-value="handleToggle(module.id, $event)" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance } from 'vue';
import api from '@/services/api';
import ToggleSwitch from '@/components/ToggleSwitch.vue';
import Swal from 'sweetalert2';

const props = defineProps({
    application: {
        type: Object,
        required: true,
        default: () => ({})
    },
    resource: {
        type: String,
        required: true
    },
    errors: {
        type: Object,
        default: () => ({})
    }
});

const { proxy } = getCurrentInstance();

const modules = ref([]);
const subscriptions = ref([]);
const moduleStates = ref({});

// Obtener la suscripción de un módulo
const getSubscription = (moduleId) => {
    return subscriptions.value.find(sub => sub.module_id === moduleId);
};

// Verificar si una suscripción está activa
const isSubscriptionActive = (moduleId) => {
    const subscription = getSubscription(moduleId);
    if (!subscription) return false;
    
    // Estados que habilitan el módulo según la documentación
    const activeStatuses = ['active', 'trial', 'grace_period'];
    return activeStatuses.includes(subscription.status);
};

// Obtener etiqueta del estado
const getStatusLabel = (status) => {
    const labels = {
        'active': 'Activo',
        'trial': 'Prueba',
        'grace_period': 'Período de Gracia',
        'suspended': 'Suspendido',
        'cancelled': 'Cancelado',
        'expired': 'Expirado'
    };
    return labels[status] || status;
};

// Actualizar estados de los toggles
const updateModuleStates = () => {
    modules.value.forEach(mod => {
        moduleStates.value[mod.id] = isSubscriptionActive(mod.id);
    });
};

// Obtener módulos disponibles
const fetchModules = async () => {
    try {
        const response = await api.get(props.resource, {
            params: {
                per_page: 50,
                sort_column: 'name',
                sort_order: 'asc'
            }
        });
        modules.value = response.data.data;
        updateModuleStates();
    } catch (error) {
        console.error('Error fetching modules:', error);
        proxy.$notify(
            'Error al cargar módulos',
            '',
            'danger',
            'ni ni-fat-remove ni-lg'
        );
    }
};

// Obtener suscripciones de la aplicación
const fetchSubscriptions = async () => {
    try {
        const response = await api.get(`/subscriptions/application/${props.application.id}`);
        
        if (response.data.success) {
            subscriptions.value = response.data.data.subscriptions || [];
            updateModuleStates();
        }
    } catch (error) {
        console.error('Error fetching subscriptions:', error);
        // No mostrar error si no hay suscripciones aún
        if (error.response?.status !== 404) {
            proxy.$notify(
                'Error al cargar suscripciones',
                '',
                'danger',
                'ni ni-fat-remove ni-lg'
            );
        }
    }
};

// Crear suscripción (Habilitar módulo)
const createSubscription = async (moduleId) => {
    const module = modules.value.find(m => m.id === moduleId);
    
    // Mostrar diálogo para configurar la suscripción
    const { value: formValues } = await Swal.fire({
        title: `Activar ${module.name}`,
        html: `
            <div style="text-align: left; padding: 0 1rem;">
                <div style="margin-bottom: 1rem;">
                    <label style="display: block; margin-bottom: 0.5rem; font-weight: 600;">
                        Cantidad de médicos:
                    </label>
                    <input id="swal-users" type="number" min="1" value="${module.pricing_info?.includes_users || 1}" 
                           class="swal2-input" style="width: 100%; margin: 0;" placeholder="Número de médicos">
                </div>
                <div style="margin-bottom: 1rem;">
                    <label style="display: block; margin-bottom: 0.5rem; font-weight: 600;">
                        Ciclo de facturación:
                    </label>
                    <select id="swal-billing" class="swal2-input" style="width: 100%; margin: 0;">
                        <option value="monthly">Mensual</option>
                        <option value="quarterly">Trimestral</option>
                        <option value="semiannual">Semestral</option>
                        <option value="annual">Anual</option>
                    </select>
                </div>
                <div style="background: #f0f4f8; padding: 1rem; border-radius: 8px; margin-top: 1rem;">
                    <p style="margin: 0; font-size: 0.9rem; text-align: center;">
                        <strong>Precio por médico:</strong> $${formatPrice(module.pricing_info?.price_per_user || 875)}/mes<br>
                        <span style="color: #6b7280; font-size: 0.85rem;">Modelo simple: Todos pagan lo mismo</span>
                    </p>
                    ${module.trial_days ? `<p style="margin: 0.5rem 0 0 0; text-align: center; color: #059669; font-weight: 600;">
                        <i class="fas fa-gift"></i> ${module.trial_days} días de prueba gratis
                    </p>` : ''}
                </div>
            </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Calcular y Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
            confirmButton: 'btn bg-gradient-success',
            cancelButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false,
        preConfirm: () => {
            const users = document.getElementById('swal-users').value;
            const billing = document.getElementById('swal-billing').value;
            
            if (!users || users < 1) {
                Swal.showValidationMessage('Ingresa al menos 1 médico');
                return false;
            }
            
            return { users: parseInt(users), billing };
        }
    });

    if (!formValues) return false;

    console.log('📦 Módulo seleccionado:', {
        id: module.id,
        name: module.name,
        pricing: module.pricing
    });

    try {
        // Calcular precio
        const priceResponse = await api.get('/subscriptions/calculate-price', {
            params: {
                module_id: moduleId,
                user_count: formValues.users,
                billing_cycle: formValues.billing
            }
        });

        console.log('📊 Respuesta de calculate-price:', priceResponse.data);

        // Verificar que llegó la respuesta correctamente
        if (!priceResponse.data || !priceResponse.data.pricing) {
            throw new Error('No se pudo calcular el precio. Respuesta inválida del servidor.');
        }

        const pricing = priceResponse.data.pricing;

        // Confirmar creación con precio calculado
        const confirmResult = await Swal.fire({
            title: '¿Confirmar suscripción?',
            html: `
                <div style="text-align: left; padding: 0 1rem;">
                    <h4 style="margin-bottom: 1rem;">${module.name}</h4>
                    <div style="background: #f8f9fa; padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
                        <p style="margin: 0.5rem 0;"><strong>Cantidad de médicos:</strong> ${pricing.user_count || formValues.users}</p>
                        <p style="margin: 0.5rem 0;"><strong>Precio por médico:</strong> $${formatPrice(pricing.price_per_user)}/mes</p>
                        <p style="margin: 0.5rem 0;"><strong>Ciclo de facturación:</strong> ${getBillingCycleLabel(pricing.billing_cycle || formValues.billing)}</p>
                        <hr style="margin: 0.5rem 0;">
                        <p style="margin: 0.5rem 0; font-size: 1.2rem; color: #28a745;">
                            <strong>Total mensual:</strong> $${formatPrice(pricing.final_price)}
                        </p>
                        <p style="margin: 0.5rem 0; font-size: 0.95rem; color: #6b7280;">
                            Total anual: $${formatPrice(pricing.final_price * 12)}
                        </p>
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, activar',
            cancelButtonText: 'Cancelar',
            customClass: {
                confirmButton: 'btn bg-gradient-success',
                cancelButton: 'btn bg-gradient-danger'
            },
            buttonsStyling: false
        });

        if (!confirmResult.isConfirmed) return false;

        // Crear suscripción
        console.log('📤 Enviando datos para crear suscripción:', {
            application_id: props.application.id,
            module_id: moduleId,
            subscription_type: 'per_application',
            contracted_users: formValues.users,
            billing_cycle: formValues.billing,
            trial_days: module.trial_days || 0,
            auto_renew: true
        });

        const response = await api.post('/subscriptions', {
            application_id: props.application.id,
            module_id: moduleId,
            subscription_type: 'per_application',
            contracted_users: formValues.users,
            billing_cycle: formValues.billing,
            trial_days: module.trial_days || 0,
            auto_renew: true
        });

        console.log('📥 Respuesta de crear suscripción:', response);
        console.log('📥 response.data:', response?.data);

        // Manejar diferentes estructuras de respuesta
        const isSuccess = response?.data?.success === true || 
                         response?.status === 201 || 
                         response?.status === 200;

        if (isSuccess && response?.data) {
            const message = response.data.message || 
                          'Módulo habilitado exitosamente';
            
            proxy.$notify(
                message,
                '',
                'success',
                'ni ni-check-bold ni-lg'
            );
            
            await fetchSubscriptions();
            return true;
        } else {
            console.error('❌ Respuesta no válida:', {
                hasResponse: !!response,
                hasData: !!response?.data,
                hasSuccess: response?.data?.success,
                status: response?.status,
                fullResponse: response
            });
            throw new Error('Respuesta inválida del servidor al crear suscripción');
        }
    } catch (error) {
        console.error('❌ Error al crear suscripción:', error);
        console.error('Error completo:', error.response || error);
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error ||
                           error.message || 
                           'No se pudo activar el módulo';
        
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            customClass: {
                confirmButton: 'btn bg-gradient-danger'
            },
            buttonsStyling: false
        });
    }
    
    return false;
};

// Cancelar suscripción (Deshabilitar módulo)
const cancelSubscription = async (moduleId) => {
    const subscription = getSubscription(moduleId);
    if (!subscription) return false;

    const result = await Swal.fire({
        title: '¿Cancelar suscripción?',
        html: `
            <p>¿Estás seguro de cancelar la suscripción a <strong>${subscription.module_name}</strong>?</p>
            <p style="margin-top: 1rem;">
                <label style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
                    <input type="checkbox" id="swal-immediate" style="width: auto;">
                    <span>Cancelar inmediatamente (no esperar al fin del período)</span>
                </label>
            </p>
            <textarea id="swal-reason" class="swal2-textarea" placeholder="Motivo de cancelación (opcional)" 
                      style="width: 100%; margin-top: 1rem;"></textarea>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, cancelar',
        cancelButtonText: 'No',
        customClass: {
            confirmButton: 'btn bg-gradient-warning',
            cancelButton: 'btn bg-gradient-secondary'
        },
        buttonsStyling: false,
        preConfirm: () => {
            return {
                immediate: document.getElementById('swal-immediate').checked,
                reason: document.getElementById('swal-reason').value
            };
        }
    });

    if (!result.isConfirmed) return false;

    try {
        const response = await api.post(`/subscriptions/${subscription.id}/cancel`, {
            reason: result.value.reason,
            immediate: result.value.immediate
        });

        if (response.data.success) {
            proxy.$notify(
                response.data.message || 'Módulo deshabilitado exitosamente',
                '',
                'warning',
                'ni ni-fat-remove ni-lg'
            );
            
            await fetchSubscriptions();
            return true;
        }
    } catch (error) {
        console.error('Error al cancelar suscripción:', error);
        
        const errorMessage = error.response?.data?.message || 
                           'No se pudo cancelar la suscripción';
        
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            customClass: {
                confirmButton: 'btn bg-gradient-danger'
            },
            buttonsStyling: false
        });
    }
    
    return false;
};

// Reactivar suscripción
const reactivateSubscription = async (moduleId) => {
    const subscription = getSubscription(moduleId);
    if (!subscription) return false;

    const result = await Swal.fire({
        title: '¿Reactivar suscripción?',
        html: `<p>¿Deseas reactivar la suscripción a <strong>${subscription.module_name}</strong>?</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, reactivar',
        cancelButtonText: 'Cancelar',
        customClass: {
            confirmButton: 'btn bg-gradient-success',
            cancelButton: 'btn bg-gradient-secondary'
        },
        buttonsStyling: false
    });

    if (!result.isConfirmed) return false;

    try {
        const response = await api.post(`/subscriptions/${subscription.id}/reactivate`);

        if (response.data.success) {
            proxy.$notify(
                response.data.message || 'Módulo reactivado exitosamente',
                '',
                'success',
                'ni ni-check-bold ni-lg'
            );
            
            await fetchSubscriptions();
            return true;
        }
    } catch (error) {
        console.error('Error al reactivar suscripción:', error);
        
        const errorMessage = error.response?.data?.message || 
                           'No se pudo reactivar la suscripción';
        
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            customClass: {
                confirmButton: 'btn bg-gradient-danger'
            },
            buttonsStyling: false
        });
    }
    
    return false;
};

// Manejar toggle
const handleToggle = async (moduleId, value) => {
    const subscription = getSubscription(moduleId);
    
    let success = false;
    
    if (value) {
        // Activar: verificar si existe suscripción cancelada/expirada para reactivar
        if (subscription && ['cancelled', 'expired', 'suspended'].includes(subscription.status)) {
            success = await reactivateSubscription(moduleId);
        } else {
            // Crear nueva suscripción
            success = await createSubscription(moduleId);
        }
    } else {
        // Desactivar: cancelar suscripción
        success = await cancelSubscription(moduleId);
    }
    
    // Si la operación falló, revertir el estado del toggle
    if (!success) {
        moduleStates.value[moduleId] = !value;
    }
};

// Helper para etiquetas de ciclo de facturación
const getBillingCycleLabel = (cycle) => {
    const labels = {
        'monthly': 'Mensual',
        'quarterly': 'Trimestral',
        'semiannual': 'Semestral',
        'annual': 'Anual'
    };
    return labels[cycle] || cycle;
};

// Helper para formatear precios
const formatPrice = (value) => {
    if (value === null || value === undefined) return '0.00';
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
};

onMounted(async () => {
    await Promise.all([
        fetchModules(),
        fetchSubscriptions()
    ]);
});

watch(() => props.application.id, async () => {
    await fetchSubscriptions();
});
</script>

<style scoped>
.modules-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
    padding: 0.5rem;
}

.module-card {
    position: relative;
    background-color: #ffffff;
    border: 1px solid #dde6f0;
    border-radius: 14px;
    padding: 1.25rem 1rem 1rem 1rem;
    min-height: 140px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.3s ease;
}

.module-card:hover {
    background-color: #f8fafc;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
}

/* Cuando el módulo NO está habilitado */
.module-disabled {
    opacity: 0.5;
    filter: grayscale(60%);
}

.module-disabled:hover {
    transform: none;
}

.module-action {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
}

.module-content {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
}

.module-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
    flex-shrink: 0;
}

.module-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    padding-right: 3rem;
}

.module-name {
    font-size: 1.05rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.3;
    margin-bottom: 0.25rem;
}

.module-description {
    font-size: 0.8rem;
    color: #6b7280;
    line-height: 1.4;
    display: block;
}

.subscription-info {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-top: 0.5rem;
}

.module-pricing-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.25rem;
}

.module-users,
.module-price {
    font-size: 0.85rem;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.module-users i,
.module-price i {
    color: #6366f1;
    font-size: 0.75rem;
}

.status-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 0.25rem;
    width: fit-content;
}

.status-badge.active {
    background-color: #d1fae5;
    color: #065f46;
}

.status-badge.trial {
    background-color: #dbeafe;
    color: #1e40af;
}

.status-badge.grace_period {
    background-color: #fef3c7;
    color: #92400e;
}

.status-badge.suspended,
.status-badge.cancelled,
.status-badge.expired {
    background-color: #fee2e2;
    color: #991b1b;
}

.modules-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #1f2937;
    text-align: center;
    margin: 0.75rem 0;
}

.modules-header {
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
}
</style>
